import {
  Badge,
  Box,
  Button,
  Card,
  Field,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Input,
  RefreshButton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  notify,
} from "components/ui";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import {
  CustomerCompanySkill,
  customerCompanyAdminService,
} from "services/customerCompanyAdminService";
import { getThemeColor } from "theme/colors";
import { formatDate } from "./format";

interface SkillsTabProps {
  customerCompanyId: string;
}

interface SkillForm {
  name: string;
  category: string;
  humanReadableDescription: string;
  triggerContext: string;
  isActive: boolean;
}

const EMPTY_FORM: SkillForm = {
  name: "",
  category: "",
  humanReadableDescription: "",
  triggerContext: "",
  isActive: true,
};

/**
 * Custom skills scoped to this company. These are CustomerCompanySkill rows, separate from the
 * tenant-wide skills at /admin/skills — a skill here only affects this company's conversations.
 */
const SkillsTab = ({ customerCompanyId }: SkillsTabProps) => {
  const { t } = useTranslation();

  const [skills, setSkills] = useState<CustomerCompanySkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerCompanySkill | null>(null);
  const [form, setForm] = useState<SkillForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<CustomerCompanySkill | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSkills(
        await customerCompanyAdminService.listSkills(customerCompanyId),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.skills.loadFailed", {
            defaultValue: "Failed to load skills.",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reportFailure = (err: any) =>
    notify({
      type: "error",
      title: t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      description: err.response?.data?.error,
    });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (skill: CustomerCompanySkill) => {
    setEditing(skill);
    setForm({
      name: skill.name,
      category: skill.category ?? "",
      humanReadableDescription: skill.humanReadableDescription,
      triggerContext: skill.triggerContext,
      isActive: skill.isActive,
    });
    setFormError(null);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (
      !form.name.trim() ||
      !form.humanReadableDescription.trim() ||
      !form.triggerContext.trim()
    ) {
      setFormError(
        t("app.customerCompanies.skills.requiredFields", {
          defaultValue: "Name, description and trigger context are required.",
        }),
      );
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        humanReadableDescription: form.humanReadableDescription.trim(),
        triggerContext: form.triggerContext.trim(),
        isActive: form.isActive,
      };
      if (editing) {
        const updated = await customerCompanyAdminService.updateSkill(
          customerCompanyId,
          editing.id,
          payload,
        );
        setSkills((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        );
      } else {
        const created = await customerCompanyAdminService.createSkill(
          customerCompanyId,
          payload,
        );
        setSkills((prev) => [...prev, created]);
      }
      setEditorOpen(false);
      notify({
        type: "success",
        title: t("app.customerCompanies.skills.saved", {
          defaultValue: "Skill saved.",
        }),
      });
    } catch (err: any) {
      setFormError(
        err.response?.data?.error ||
          t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (
    skill: CustomerCompanySkill,
    isActive: boolean,
  ) => {
    setPendingId(skill.id);
    try {
      const updated = await customerCompanyAdminService.updateSkill(
        customerCompanyId,
        skill.id,
        { isActive },
      );
      setSkills((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customerCompanyAdminService.deleteSkill(
        customerCompanyId,
        deleteTarget.id,
      );
      setSkills((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      notify({
        type: "success",
        title: t("app.customerCompanies.skills.deleted", {
          defaultValue: "Skill deleted.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Card.Root
      borderWidth="1px"
      borderColor={getThemeColor("card.border")}
      borderRadius="md"
    >
      <Card.Body>
        <Stack gap={3}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Stack gap={1}>
              <Heading size="sm">
                {t("app.customerCompanies.skills.title", {
                  defaultValue: "Skills",
                })}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.skills.subtitle", {
                  defaultValue:
                    "Custom behaviours the assistant applies for this company only.",
                })}
              </Text>
            </Stack>
            <HStack gap={2}>
              <RefreshButton onClick={load} loading={loading} />
              <Button intent="primary" onClick={openCreate}>
                <MdAdd />{" "}
                {t("app.customerCompanies.skills.add", {
                  defaultValue: "Add skill",
                })}
              </Button>
            </HStack>
          </Flex>

          {error && (
            <InfoBox variant="error">
              <Text color="red.700">{error}</Text>
            </InfoBox>
          )}

          {loading ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.skills.loading", {
                defaultValue: "Loading skills…",
              })}
            </Text>
          ) : skills.length === 0 ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.skills.empty", {
                defaultValue: "No skills defined for this company yet.",
              })}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.skills.columns.name", {
                        defaultValue: "Name",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.skills.columns.category", {
                        defaultValue: "Category",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.skills.columns.trigger", {
                        defaultValue: "Triggers when",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.common.active", { defaultValue: "Active" })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.skills.columns.updated", {
                        defaultValue: "Updated",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.common.actions", { defaultValue: "Actions" })}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {skills.map((skill) => (
                    <Table.Row key={skill.id}>
                      <Table.Cell>
                        <Stack gap={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {skill.name}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="var(--text-secondary-color)"
                          >
                            {skill.humanReadableDescription}
                          </Text>
                        </Stack>
                      </Table.Cell>
                      <Table.Cell>
                        {skill.category ? (
                          <Badge variant="subtle">{skill.category}</Badge>
                        ) : (
                          "—"
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs">{skill.triggerContext}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Switch
                          checked={skill.isActive}
                          disabled={pendingId === skill.id}
                          onCheckedChange={(e: any) =>
                            handleToggleActive(skill, e.checked)
                          }
                        />
                      </Table.Cell>
                      <Table.Cell>{formatDate(skill.updatedAt)}</Table.Cell>
                      <Table.Cell>
                        <HStack gap={1}>
                          <Button
                            intent="ghost"
                            onClick={() => openEdit(skill)}
                          >
                            <MdEdit />{" "}
                            {t("app.common.edit", { defaultValue: "Edit" })}
                          </Button>
                          <Button
                            intent="ghost"
                            onClick={() => setDeleteTarget(skill)}
                          >
                            <MdDelete />{" "}
                            {t("app.common.delete", { defaultValue: "Delete" })}
                          </Button>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Stack>
      </Card.Body>

      <DialogRoot
        open={editorOpen}
        onOpenChange={(e: { open: boolean }) =>
          e.open ? undefined : setEditorOpen(false)
        }
        size="lg"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("app.customerCompanies.skills.edit", {
                    defaultValue: "Edit skill",
                  })
                : t("app.customerCompanies.skills.add", {
                    defaultValue: "Add skill",
                  })}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={4}>
              {formError && (
                <InfoBox variant="error">
                  <Text color="red.700">{formError}</Text>
                </InfoBox>
              )}

              <Field
                label={t("app.customerCompanies.skills.fields.name", {
                  defaultValue: "Name",
                })}
                required
              >
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  maxLength={200}
                />
              </Field>

              <Field
                label={t("app.customerCompanies.skills.fields.category", {
                  defaultValue: "Category (optional)",
                })}
              >
                <Input
                  value={form.category}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, category: e.target.value }))
                  }
                  maxLength={100}
                />
              </Field>

              <Field
                label={t("app.customerCompanies.skills.fields.description", {
                  defaultValue: "What the skill does",
                })}
                helperText={t(
                  "app.customerCompanies.skills.fields.descriptionHelp",
                  {
                    defaultValue:
                      "Plain-language instruction the assistant follows when this skill applies.",
                  },
                )}
                required
              >
                <Textarea
                  value={form.humanReadableDescription}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanReadableDescription: e.target.value,
                    }))
                  }
                  rows={4}
                />
              </Field>

              <Field
                label={t("app.customerCompanies.skills.fields.trigger", {
                  defaultValue: "Triggers when",
                })}
                helperText={t(
                  "app.customerCompanies.skills.fields.triggerHelp",
                  {
                    defaultValue:
                      "Describe the situation that should activate this skill, e.g. 'the customer asks about an invoice'.",
                  },
                )}
                required
              >
                <Textarea
                  value={form.triggerContext}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      triggerContext: e.target.value,
                    }))
                  }
                  rows={3}
                  maxLength={2000}
                />
              </Field>

              <HStack justify="space-between">
                <Text fontWeight="medium">
                  {t("app.common.active", { defaultValue: "Active" })}
                </Text>
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(e: any) =>
                    setForm((prev) => ({ ...prev, isActive: e.checked }))
                  }
                />
              </HStack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button intent="secondary" onClick={() => setEditorOpen(false)}>
              {t("app.common.cancel")}
            </Button>
            <Button intent="primary" onClick={handleSave} disabled={saving}>
              {saving
                ? t("app.common.saving", { defaultValue: "Saving..." })
                : t("app.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t("app.customerCompanies.skills.deleteTitle", {
          defaultValue: "Delete skill",
        })}
        message={t("app.customerCompanies.skills.deleteConfirm", {
          defaultValue:
            "This skill will no longer be applied to this company's conversations.",
        })}
      />
    </Card.Root>
  );
};

export default SkillsTab;
